import React, { ReactNode } from 'react';

import type { Picture as PictureType } from '@root/sanity/sanity.types';
import { urlFor } from '@root/lib/sanity';
import Picture from './Picture';
import GridComponent from 'components/GridComponent';

export type NumberedListItem = {
  content: ReactNode;
  headerText: string;
  image?: PictureType;
};

type NumberedListProps = {
  className?: string;
  items: NumberedListItem[];
  content?: ReactNode;
  twoColumnGrid?: boolean;
};

const NumberedList = ({ className = '', items, content, twoColumnGrid = false }: NumberedListProps) => {
  return !twoColumnGrid ? (
    <div className={`container mt-64 md:mt-128 ${className}`}>
      {content}
      {items.map((item: NumberedListItem, index) => {
        return (
          item?.image && (
            <div className="row space-y-32 pr-64 sm:py-64" key={item.headerText}>
              <div className="hidden lg:col-span-1 lg:block" />

              <p className="col-span-2 sm:col-span-1 md:col-span-2">{`${index + 1}`.padStart(2, '0')}</p>

              <Picture
                alt={item.image.alt || ''}
                className="col-span-10 xs:col-span-8 sm:col-span-5 md:col-span-4"
                polaroid={item.image.polaroid}
                url={urlFor(item.image.asset).url()}
                imageType="PNG"
              />

              <div className="col-span-2 sm:hidden md:col-span-1 md:block" />

              <div className="xs:col-span-2 sm:hidden" />

              <div className="col-span-10 col-start-3 space-y-16 sm:col-span-6 md:col-span-5 lg:col-span-4">
                <h4 className="text-h4-display">{item.headerText}</h4>

                <div>{item.content}</div>
              </div>

              <div className="col-span-2 sm:hidden md:col-span-1 md:block" />
            </div>
          )
        );
      })}
    </div>
  ) : (
    <div className={`container mt-64 md:mt-128 ${className}`}>
      <div className="mb-32 sm:pl-64">
        {content}

        <div className="grid grid-cols-1 gap-64 sm:ml-128 sm:grid-cols-2 md:gap-16 md:pl-64">
          {items.map((item, index) => {
            return (
              item?.image && (
                <GridComponent
                  url={urlFor(item.image.asset).url()}
                  alt={item.image.alt || ''}
                  index={index + 1}
                  title={item.headerText}
                  className="md:px-72 px-32"
                >
                  <div className="md:pr-32">{item.content}</div>
                </GridComponent>
              )
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NumberedList;
